import React, {useEffect, useState} from 'react';
import {preferenceSaveService} from '../../services/preferenceSaveService';
import {propertiesStorage} from '../../services/propertiesStorage';
import ProductPropertyNumberValue from '../productsTab/products/ProductPropertyNumberValue';
import ProductPropertyInput from '../productsTab/products/ProductPropertyInput';
import ProductPropertyBoolean from '../productsTab/products/ProductPropertyBoolean';
import ProductPropertySelect from '../productsTab/products/ProductPropertySelect';
import {createSelectPreferencePropertyService} from '../../services/preferenceSelectService';
import {PROPERTY_TYPE_BOOLEAN, PROPERTY_TYPE_NUMBER, PROPERTY_TYPE_SELECT, PROPERTY_TYPE_STRING,} from '../../constant';
import { useTranslation } from 'react-i18next';
import { Select } from '@chapche/ui';
import {PreferenceType} from "../../services/preferenceTypes";
import {RecipeCharacteristicCategory, RecipeSource, RecipeTaste} from "../../services/recipeTypes";
import {ProductCategory} from "../../productTypes";
import {Units} from "../../services/mealTypes";
import PreferenceStructureNumberValue from "./PreferenceStructureNumberValue";

const findPropertyByCode = (itemCode) => propertiesStorage.getItems(true).find(it => it.propertyName === itemCode);

const isProperty = (parentMode: boolean, index: number) => {
    if(parentMode) {
        return preferenceSaveService.getPreference().preferenceType === PreferenceType.PROPERTY;
    } else {
        return preferenceSaveService.getPreference().combinedPreferences[index].preferenceType === PreferenceType.PROPERTY;
    }
};

const isRecipeCharacteristic = (parentMode: boolean, index: number): boolean => {
    if(parentMode) {
        return preferenceSaveService.getPreference().preferenceType === PreferenceType.RECIPE_CHARACTERISTIC;
    } else {
        return preferenceSaveService.getPreference().combinedPreferences[index].preferenceType === PreferenceType.RECIPE_CHARACTERISTIC;
    }
};

const isMacronutrientType = (parentMode: boolean, index: number): boolean => {
    if(parentMode) {
        return preferenceSaveService.getPreference().preferenceType === PreferenceType.MACRONUTRIENT_PER_MEAL
            || preferenceSaveService.getPreference().preferenceType === PreferenceType.MACRONUTRIENT_PER_DAY;
    } else {
        return preferenceSaveService.getPreference().combinedPreferences[index].preferenceType === PreferenceType.MACRONUTRIENT_PER_MEAL
            || preferenceSaveService.getPreference().combinedPreferences[index].preferenceType === PreferenceType.MACRONUTRIENT_PER_DAY;
    }
}

const isProductCategoryType = (parentMode: boolean, index: number): boolean => {
    if(parentMode) {
        return preferenceSaveService.getPreference().preferenceType === PreferenceType.PRODUCT_CATEGORY_PER_DAY
            || preferenceSaveService.getPreference().preferenceType === PreferenceType.PRODUCT_CATEGORY_PER_MEAL;
    } else {
        return preferenceSaveService.getPreference().combinedPreferences[index].preferenceType === PreferenceType.PRODUCT_CATEGORY_PER_DAY
            || preferenceSaveService.getPreference().combinedPreferences[index].preferenceType === PreferenceType.PRODUCT_CATEGORY_PER_MEAL;
    }
}

const PreferenceStructureValue = ({parentMode, index, preferenceStructureItem, handleUpdatePreferenceItem}) => {
    const { i18n } = useTranslation();
    const [value, setValue] = useState<string>(null);

    useEffect(() => {
        console.log("----------------------------PreferenceStructureValue");
        console.log(preferenceStructureItem);
        if (!preferenceStructureItem) {
            return;
        }
        setValue(preferenceStructureItem.value);
    }, [preferenceStructureItem]);

    const buildUnitOptions = () => {
        return Object.values(Units).map((it) => {
            return {
                value: it,
                label: i18n.t(`meal.scheme.unit.${it.toLowerCase()}`),
            };
        })
    };

    const buildRecipeCharacteristics = (itemCode: string) => {
        if(itemCode === RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_TASTE) {
            return Object.values(RecipeTaste).map(it => {
                return {
                    value: it,
                    label: i18n.t(`recipe.scheme.characteristic.taste.${it.toLowerCase()}`),
                };
            });
        }
        if(itemCode == RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_SOURCE) {
            return Object.values(RecipeSource).map(it => {
                return {
                    value: it,
                    label: i18n.t(`recipe.scheme.characteristic.source.${it.toLowerCase()}`),
                };
            });
        }
        if(itemCode === RecipeCharacteristicCategory.RECIPE_CHARACTERISTIC_PRODUCT_CATEGORY) {
            return Object.values(ProductCategory).map(it => {
                return {
                    value: it,
                    label: i18n.t(`product.scheme.formGroup.${it.toLowerCase()}`),
                };
            });
        }
        return [];
    };

    const findCurrentValueType = () => {
        const property = findPropertyByCode(preferenceSaveService.getPreference().preferenceItemCodes[0].itemCode);
        if(property) {
            switch (property.propertyType) {
            case PROPERTY_TYPE_NUMBER: {
                return (
                    <div className="product__form-block">
                        <ProductPropertyNumberValue 
                            propertyId={null} 
                            fieldName={'minValue'} 
                            fieldValue={preferenceSaveService.getPreference().preferenceItemCodes[0].minValue}
                            itemSaveService={preferenceSaveService} />
                        <ProductPropertyNumberValue 
                            propertyId={null} 
                            fieldName={'maxValue'} 
                            fieldValue={preferenceSaveService.getPreference().preferenceItemCodes[0].maxValue}
                            itemSaveService={preferenceSaveService} />
                    </div>
                );
            }
            case PROPERTY_TYPE_STRING: {
                return <>
                    <span className="preference__form-value">{i18n.t('product.scheme.property.code.valueOfData')}</span>
                    <ProductPropertyInput propertyId={property.id} itemSaveService={preferenceSaveService} />
                </>;
            }
            case PROPERTY_TYPE_BOOLEAN: {
                return <ProductPropertyBoolean propertyId={null} itemSaveService={preferenceSaveService} />;
            }
            case PROPERTY_TYPE_SELECT: {
                return <>
                    <span className="preference__form-value">{i18n.t('product.scheme.property.code.valueOfData')}</span>
                    <ProductPropertySelect selectWithTokensManager={createSelectPreferencePropertyService(property.id)}/>
                </>;
            }
            default: {
                return null;
            }}
        }
    };

    const updatePreferenceValue = (value: string) => {
        setValue(value);
        handleUpdatePreferenceItem("value", value);
    };

    if (!preferenceStructureItem) {
        return null;
    }
    if (isProperty(parentMode, index)) {
        return (
            <React.Fragment key={'preferencePropertyValue'}>
                {findCurrentValueType()}
            </React.Fragment>
        );
    } else if (isMacronutrientType(parentMode, index) || isProductCategoryType(parentMode, index)) {
        return (
            <div className="product__form-block">
                <Select
                    fullWidth
                    className="mt-0"
                    value={value}
                    options={buildUnitOptions()}
                    onChange={updatePreferenceValue}
                />
                <PreferenceStructureNumberValue
                    field={"minValue"}
                    preferenceStructureItem={preferenceStructureItem}
                    handleUpdatePreferenceItem={handleUpdatePreferenceItem}/>
                <PreferenceStructureNumberValue
                    field={"maxValue"}
                    preferenceStructureItem={preferenceStructureItem}
                    handleUpdatePreferenceItem={handleUpdatePreferenceItem}/>
            </div>
            );
    } else if (isRecipeCharacteristic(parentMode, index)) {
        return (
            <React.Fragment key={'preferencePropertyValue'}>
                <Select
                    fullWidth
                    className="mt-0"
                    value={value}
                    options={buildRecipeCharacteristics(preferenceStructureItem.itemCode)}
                    onChange={updatePreferenceValue}
                />
            </React.Fragment>
        );
    } else {
        return null;
    }
};

export default PreferenceStructureValue;
